import { Bookmark, Share2, MapPin, MessageCircle, Tag as TagIcon, Edit } from "lucide-react";
import { useState, useEffect } from "react";
import CommentsDialog from "../../Comments";
import axiosInstance from "../../../api/axiosInstance";
import TagDialogWrapper from "./TagDialogWrapper";
import ModalPortal from "../ui/ModalPortal";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField } from '@mui/material';

export default function StickyPlaceIcons({ trippinId, place, source }) {
  console.log('[StickyPlaceIcons] props:', { trippinId, place: place && { place_name: place.place_name, is_saved: place.is_saved, post_place_ids: place.post_place_ids } });
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [isSaved, setIsSaved] = useState(place.is_saved);
  const postPlaceId = place.post_place_ids?.[0] || place?.id;
  const placeName = place.place_name;
  const ICON_SIZE = 26;
  const ICON_CLASS = "drop-shadow-md";
  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  const [editPlaceDialogOpen, setEditPlaceDialogOpen] = useState(false);
  const [editedPlaceName, setEditedPlaceName] = useState('');
  const [editedLocality, setEditedLocality] = useState({
    city: '',
    state: '',
    country: '',
    locality1: '',
    locality2: '',
    locality3: ''
  });
  const isMyTrippins = source === "mytrippins";
  const isTaggedTrippins = source === "taggedtrippins";

  const toggleSave = async () => {
    if (!postPlaceId) return;

    try {
      if (isSaved) {
        await axiosInstance.delete(`/trippins/places/${place.id}/save`, {
          data: { post_place_id: postPlaceId },
        });
      } else {
        await axiosInstance.post(`/trippins/places/${place.id}/save`, {
          post_place_id: postPlaceId,
        });
      }

      setIsSaved(!isSaved);
    } catch (err) {
      console.error("Error toggling saved state:", err);
    }
  };

  const share = async () => {
    if (!postPlaceId) return;

    try {
      await axiosInstance.post(`/trippins/places/${place.id}/share`, {
        post_place_id: postPlaceId,
      });
    } catch (err) {
      console.error("Error sharing place:", err);
    }
  };

  const openMaps = () => {
    const { latitude, longitude } = place;
    if (latitude && longitude) {
      const mapsUrl = `https://www.google.com/maps?q=${latitude},${longitude}`;
      window.open(mapsUrl, "_blank");
    }
  };

  const tagUsersToPlace = async (userIds, fullUsers) => {
    try {
      if (!postPlaceId) return;

      await axiosInstance.put(
        `/mytrippins/${trippinId}/tagPlace/${place.id}`,
        { userIds } // allow empty array
      );

      place.taggedUsers = [...fullUsers];
      setTagDialogOpen(false);
    } catch (err) {
      console.error("Error tagging users:", err);
    }
  };

  const openEditPlaceDialog = () => {
    if (place.source === 'osm') {
      alert('OSM places cannot be edited. This helps maintain data integrity.');
      return;
    }

    setEditedPlaceName(place.place_name || '');
    setEditedLocality({
      city: place.locality?.city || '',
      state: place.locality?.state || '',
      country: place.locality?.country || '',
      locality1: place.locality?.locality1 || '',
      locality2: place.locality?.locality2 || '',
      locality3: place.locality?.locality3 || ''
    });
    setEditPlaceDialogOpen(true);
  };

  const saveEditedPlace = async () => {
    if (!editedPlaceName.trim()) {
      alert('Please enter a place name');
      return;
    }

    try {
      await axiosInstance.put(`/places/${place.id}/edit`, {
        name: editedPlaceName.trim(),
        locality: editedLocality
      });

      place.place_name = editedPlaceName.trim();
      place.locality = { ...editedLocality };

      setEditPlaceDialogOpen(false);
      alert('Place updated successfully!');
    } catch (err) {
      console.error('Error updating place:', err);
      alert('Failed to update place. Please try again.');
    }
  };

  return (
    <>
      <div className="absolute top-1/2 -right-1 w-12 -translate-y-1/2 flex flex-col gap-4 z-50 text-white">
        {isMyTrippins ? (
          <>
            <button onClick={openMaps} className="icon-btn">
              <MapPin size={ICON_SIZE} className={ICON_CLASS} />
            </button>
            <button onClick={openEditPlaceDialog} className="icon-btn">
              <Edit size={ICON_SIZE} className={ICON_CLASS} />
            </button>
            <button onClick={() => setTagDialogOpen(true)} className="icon-btn">
              <TagIcon size={ICON_SIZE} className={ICON_CLASS} />
            </button>
          </>
        )
          : isTaggedTrippins ? (
            <button onClick={openMaps} className="icon-btn">
              <MapPin size={ICON_SIZE} className={ICON_CLASS} />
            </button>
          ) : (
            <>
              <button onClick={toggleSave} className="icon-btn">
                <Bookmark size={ICON_SIZE} className={ICON_CLASS} fill={isSaved ? "currentColor" : "none"} />
              </button>
              <button onClick={() => setCommentsOpen(true)} className="icon-btn">
                <MessageCircle size={ICON_SIZE} className={ICON_CLASS} />
              </button>
              <button onClick={openMaps} className="icon-btn">
                <MapPin size={ICON_SIZE} className={ICON_CLASS} />
              </button>
              <button onClick={share} className="icon-btn">
                <Share2 size={ICON_SIZE} className={ICON_CLASS} />
              </button>
            </>
          )}
      </div>
      {commentsOpen && postPlaceId && (
        <ModalPortal>
          <CommentsDialog
            onClose={() => setCommentsOpen(false)}
            trippinPostId={trippinId}
            placeName={placeName}
            commentType="trippin"
            id={postPlaceId}
          />
        </ModalPortal>

      )}
      {/* place-specific tag dialog (myTrippins only) */}
      {tagDialogOpen && (
        <ModalPortal>
          <TagDialogWrapper
            open={tagDialogOpen}
            onClose={() => setTagDialogOpen(false)}
            existingTags={place?.taggedUsers ?? []}
            onConfirm={tagUsersToPlace}
          />
        </ModalPortal>

      )}

      {/* Place editing dialog */}
      {editPlaceDialogOpen && (
        <ModalPortal>
          <Dialog open={editPlaceDialogOpen} onClose={() => setEditPlaceDialogOpen(false)} fullWidth maxWidth="sm">
            <DialogTitle>Edit Place</DialogTitle>
            <DialogContent>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 8 }}>
                <TextField
                  label="Place Name"
                  value={editedPlaceName}
                  onChange={(e) => setEditedPlaceName(e.target.value)}
                  fullWidth
                  required
                />

                <div style={{ fontSize: 16, fontWeight: 'bold', color: '#333', marginTop: 8 }}>
                  Locality Information
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <TextField
                    label="City"
                    value={editedLocality.city}
                    onChange={(e) => setEditedLocality(prev => ({ ...prev, city: e.target.value }))}
                    placeholder="Enter city name"
                    size="small"
                  />
                  <TextField
                    label="State/Province"
                    value={editedLocality.state}
                    onChange={(e) => setEditedLocality(prev => ({ ...prev, state: e.target.value }))}
                    placeholder="Enter state/province"
                    size="small"
                  />
                </div>

                <TextField
                  label="Country"
                  value={editedLocality.country}
                  onChange={(e) => setEditedLocality(prev => ({ ...prev, country: e.target.value }))}
                  placeholder="Enter country name"
                  size="small"
                />

                <div style={{ fontSize: 14, color: '#666', marginTop: 8 }}>
                  Additional Locality Levels (Optional)
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                  <TextField
                    label="Locality 1"
                    value={editedLocality.locality1}
                    onChange={(e) => setEditedLocality(prev => ({ ...prev, locality1: e.target.value }))}
                    placeholder="District/Area"
                    size="small"
                  />
                  <TextField
                    label="Locality 2"
                    value={editedLocality.locality2}
                    onChange={(e) => setEditedLocality(prev => ({ ...prev, locality2: e.target.value }))}
                    placeholder="Neighborhood"
                    size="small"
                  />
                  <TextField
                    label="Locality 3"
                    value={editedLocality.locality3}
                    onChange={(e) => setEditedLocality(prev => ({ ...prev, locality3: e.target.value }))}
                    placeholder="Sub-area"
                    size="small"
                  />
                </div>

                <div style={{ fontSize: 12, color: '#fff', marginTop: 8 }}>
                  Help improve our community database by filling in missing locality information.
                </div>
              </div>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setEditPlaceDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={saveEditedPlace}
                disabled={!editedPlaceName.trim()}
                variant="contained"
              >
                Save Changes
              </Button>
            </DialogActions>
          </Dialog>
        </ModalPortal>
      )}
    </>
  );
}
